import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import PropertyCard from "../components/PropertyCard";
import SearchBar from "../components/SearchBar";

export default function CategoryPage({ category, heroImage, titleKey }) {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async (f = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/properties", { params: { category, ...f, limit: 50 } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const cities = useMemo(() => {
    const set = new Set(items.map((p) => p.city));
    return [...set].sort();
  }, [items]);

  const pageInfo = t.pages[titleKey];

  return (
    <main data-testid={`category-page-${category}`}>
      {/* HERO */}
      <section className="pt-16 pb-0">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          <div className="eyebrow mb-4 rise">{lang === "fr" ? "Catalogue" : "Catalog"}</div>
          <h1 className="headline text-4xl sm:text-6xl text-[#1d1d1f] max-w-3xl rise rise-delay-1">
            {pageInfo.title}
          </h1>
          <p className="text-[17px] sm:text-[20px] text-[#86868b] max-w-xl mt-5 rise rise-delay-2">
            {pageInfo.subtitle}
          </p>
        </div>
        <div className="mt-12 max-w-[1400px] mx-auto px-4">
          <div className="squircle relative aspect-[21/9] overflow-hidden bg-[#f5f5f7]">
            <img src={heroImage} alt={pageInfo.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* SEARCH */}
      <section className="py-10">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-8 -mt-14 relative z-10">
          <SearchBar
            cities={cities}
            initial={filters}
            onSearch={(f) => { setFilters(f); load(f); }}
          />
        </div>
      </section>

      {/* LIST */}
      <section className="pb-24">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-8">
          <div className="text-[13px] text-[#86868b] mb-6">
            {loading
              ? (lang === "fr" ? "Chargement…" : "Loading…")
              : `${items.length} ${lang === "fr" ? "bien" : "property"}${items.length > 1 ? (lang === "fr" ? "s" : "ies") : ""}`}
          </div>
          {items.length === 0 && !loading ? (
            <div className="text-[#86868b] text-[15px] py-20 text-center">{t.filters.noResults}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10" data-testid="property-grid">
              {items.map((p) => <PropertyCard key={p.property_id} property={p} />)}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
